import React from 'react';
import classnames from 'classnames';
import { Tooltip } from 'Components/nui';
import { SpecButtons } from 'Components/SpecButtons';
import dayjs from 'dayjs';
import * as R from 'ramda';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { ColumnTree } from '../hooks/useTable/defineColumns';
import { CounterSummaryTooltip } from 'Pages/MarketRow/Counters/CounterSummaryTooltip';
import OrderActivity from 'Pages/MarketRow/OrderActivity';
import ProductAttributesTooltip from '../ProductAttributesTooltip';

export const ProductNameColumn = ColumnTree.defineNode(
  'product-name',
  ({
    sortDirection,
    sortColumn,
    setSort,
    productById,
    solutionDivisionIds,
    expandRow,
    participants,
  }) => ({
    title: (
      <div>
        <a
          href="#products"
          className={classnames('icon-sort', 'sortem', {
            [sortDirection]: sortColumn === 'product',
          })}
          onClick={() => setSort('product')}
        >
          <FormattedMessage
            id="marketplace-table-heading-product"
            description="Table heading"
            defaultMessage="Product"
          />
        </a>
      </div>
    ),
    key: 'product',
    className: 'col-product-name-alt shaded-right',
    render: (text, record) => {
      const product = productById(record.product.id, record);

      if (!product) {
        return (
          <div className="td-content product-name">
            <h2>
              <span className="market-product-name-description-initial">
                {record.product?.name ?? 'Unknown product'}
              </span>
            </h2>
          </div>
        );
      }

      const myCounter = R.find<any /* TODO: Fix type */>(
        (item) =>
          item.division && solutionDivisionIds.includes(item.division.id)
      )(R.pathOr([], ['counters'], record));

      const broker = !!record.counters?.find((counter) => !!counter.broker?.id);

      const counters = record.counters || [];

      return (
        <>
          <div className="td-content product-name">
            <h2>
              <span className="market-product-name-description">
                {product.name}

                <Link
                  to={`/products/${product.id}/quick`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip
                    title={
                      <span className="icon-tooltip">
                        <span className="icon-info-circled"></span>
                        <FormattedMessage
                          id="view-product-details-new-window"
                          defaultMessage="View product details in new window"
                        />
                      </span>
                    }
                  >
                    <span className="nowrap">
                      &#65279;
                      <span className="icon-link-ext-alt tw:relative tw:top-px"></span>
                    </span>
                  </Tooltip>
                </Link>
              </span>
            </h2>
            {record.attributes && (
              <ProductAttributesTooltip attributes={record.attributes} />
            )}
            {(R.hasPath(['owner', 'name'], product) ||
              R.hasPath(['division', 'name'], product)) && (
              <p>
                {R.pathOr(
                  R.pathOr('n/a', ['division', 'name'], product),
                  ['owner', 'name'],
                  product
                )}
              </p>
            )}
            {record.expiry && (
              <p
                className={classnames('icon-clock', 'expiration', {
                  'closing-day':
                    dayjs(record.expiry).diff(dayjs(), 'days') <= 1,
                  'closing-hour':
                    dayjs(record.expiry).diff(dayjs(), 'hours') <= 1,
                  'closing-now':
                    dayjs(record.expiry).diff(dayjs(), 'minutes') <= 10,
                })}
              >
                <FormattedMessage
                  id="marketplace-table-cell-expiry"
                  defaultMessage="Expires {expiry}"
                  values={{ expiry: dayjs(record.expiry).fromNow() }}
                />
              </p>
            )}
            <SpecButtons product={product}>
              {record.activity && Object.keys(record.activity).length > 0 && (
                <li>
                  <OrderActivity
                    orderId={record.id}
                    activity={record.activity}
                    participants={participants}
                  />
                </li>
              )}
            </SpecButtons>
          </div>
          <div className="market-icons tw:flex tw:items-center tw:gap-1 tw:mt-2 tw:mr-2">
            {!!counters.length && (
              <CounterSummaryTooltip counters={counters}>
                <a
                  className="has-counters"
                  href="#counter"
                  onClick={expandRow('counters', record.id)}
                >
                  {myCounter && <span className="my-counter" />}
                  {broker && <span className="broker" />}
                  {counters.length}
                </a>
              </CounterSummaryTooltip>
            )}

            {record.note && record.note.length > 0 && (
              <Tooltip
                title={
                  <span className="icon-tooltip">
                    <span className="icon-info-circled" />
                    <FormattedMessage
                      id="icon-tooltip-comment"
                      defaultMessage="This {type} has a comment"
                      values={{ type: record.type }}
                    />
                  </span>
                }
              >
                <a
                  className="icon-comment has-comment"
                  href="#comment"
                  onClick={expandRow()}
                >
                  {''}
                </a>
              </Tooltip>
            )}

            {record.questions && record.questions.length > 0 && (
              <Tooltip
                title={
                  <span className="icon-tooltip">
                    <span className="icon-info-circled"></span>
                    <FormattedMessage
                      id="marketplace-table-tooltip-questions"
                      defaultMessage="This {type} has {count, plural, one {# question} other {# questions}}"
                      values={{
                        type: record.type,
                        count: record.questions.length,
                      }}
                    />
                  </span>
                }
              >
                <a
                  className="has-qa"
                  href="#qa"
                  onClick={expandRow('qa', record.id)}
                >
                  <FormattedMessage
                    id="marketplace-table-qa-link"
                    defaultMessage="Q&A"
                  />
                </a>
              </Tooltip>
            )}

            {record.attachments && record.attachments.length > 0 && (
              <Tooltip
                title={
                  <span className="icon-tooltip">
                    <span className="icon-info-circled"></span>
                    <FormattedMessage
                      id="marketplace-table-tooltip-attachments"
                      defaultMessage="This {type} has {count, plural, one {# attachment} other {# attachments}}"
                      values={{
                        type: record.type,
                        count: record.attachments.length,
                      }}
                    />
                  </span>
                }
              >
                <a
                  className="icon-attach-circled is-bundle"
                  href="#attachments"
                  onClick={expandRow()}
                >
                  {''}
                </a>
              </Tooltip>
            )}
          </div>
        </>
      );
    },
  })
);
